import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase.config';

// Listen to project status changes
export const subscribeToProjectStatus = (projectId, onStatusChange, onError) => {
  const projectRef = doc(db, 'projects', projectId);

  const unsubscribe = onSnapshot(
    projectRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        onStatusChange(data);
      }
    },
    (error) => {
      console.error('Firestore subscription error:', error);
      onError?.(error);
    }
  );

  return unsubscribe;
};

export const subscribeToExportStatus = (projectId, onStatusChange, onError) => {
  const exportStatusRef = doc(db, 'exportTasks', projectId);

  const unsubscribe = onSnapshot(
    exportStatusRef,
    (docSnapShot) => {
      if (docSnapShot.exists()) {
        const data = docSnapShot.data();
        onStatusChange(data);
      }
    },
    (error) => {
      console.error('Export status subscription error:', error);
      onError?.(error);
    }
  );

  return unsubscribe;
};

// Claim project after login
export const claimProjectAPI = async (projectId) => {
  try {
    const claimProjectFn = httpsCallable(functions, 'claimProject');
    const res = await claimProjectFn({ projectId });
    return res.data;
  } catch (error) {
    console.error('Claim project error:', error);
    throw error;
  }
};

// Get project document
export const getProjectDocument = async (projectId) => {
  try {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const projectRef = doc(db, 'projects', projectId);
    const docSnap = await getDoc(projectRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      throw new Error('Project not found');
    }
  } catch (error) {
    console.error('Get project error:', error);
    throw error;
  }
};

// Fetch PPT JSON from URL
export const fetchPPTJson = async (jsonUrl) => {
  try {
    const response = await fetch(jsonUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch JSON');
    }
    return await response.json();
  } catch (error) {
    console.error('Fetch JSON error:', error);
    throw error;
  }
};

export const fetchJsonWithRetry = async (url, options = {}) => {
  const attempts = typeof options.attempts === 'number' ? options.attempts : 10;
  const baseDelayMs = typeof options.baseDelayMs === 'number' ? options.baseDelayMs : 500;
  const maxDelayMs = typeof options.maxDelayMs === 'number' ? options.maxDelayMs : 6000;

  // optional fresh URL getter
  const getFreshUrl = typeof options.getFreshUrl === 'function' ? options.getFreshUrl : null;

  let lastErr = null;
  let fatalError = null;
  let currentUrl = url;

  for (let i = 0; i < attempts; i++) {
    try {
      const cacheBust = i === 0 ? '' : (currentUrl.includes('?') ? '&' : '?') + `cb=${Date.now()}`;
      const res = await fetch(currentUrl + cacheBust, { cache: 'no-store' });

      // On 403 try fresh URL (only on first failure to avoid loops)
      if (res.status === 403 && getFreshUrl && i === 0) {
        try {
          console.log('Received 403, attempting to refresh URL...');
          currentUrl = await getFreshUrl();
          console.log('Refreshed URL:', currentUrl);
          continue;
        } catch {
          fatalError = new Error('HTTP 403 — could not refresh URL');
          break; // fail fast — no point retrying with a broken URL
        }
      }

      if (!res.ok) throw new Error(`Failed to fetch JSON (HTTP ${res.status})`);
      return await res.json();
    } catch (e) {
      lastErr = e;
      const delay = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, i));
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw fatalError || lastErr || new Error('Failed to fetch JSON');
};
